import { KeyboardEvent, useContext, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import { getClearPhone, stringToPhone } from 'src/tools/string.tools';
import { CONFIG } from 'src/config';
import { InputContext } from 'src/contexts/input.context';
import { ParserContext } from 'src/contexts/parser.context';
import { sendError } from 'src/store/app/app.actions';
import { t } from 'src/tools/text.tools';

const TXT = t(['usePhoneInput']);

export function usePhoneInput() {
  const { value, onChange, onSubmit, isDisabled, isReadonly } = useContext(InputContext);

  const spreadsheetContext = useContext(ParserContext);

  const valueProp = value ?? spreadsheetContext.value;

  const [displayValue, setDisplayValue] = useState(valueProp ?? '');

  const dispatch = useDispatch();

  useEffect(() => {
    if (isDisabled || isReadonly || !spreadsheetContext.isActive) setDisplayValue(stringToPhone(valueProp));
  }, [isDisabled, isReadonly, valueProp, spreadsheetContext.isActive]);

  function handleOnSubmit() {
    const clearValue = getClearPhone(displayValue);

    const phoneSize = clearValue.replace('+', '').length;

    if (phoneSize && phoneSize < CONFIG.minPhoneSize) dispatch(sendError(TXT('phoneNumberIsTooShort')));
    else if (phoneSize > CONFIG.maxPhoneSize) dispatch(sendError(TXT('phoneNumberIsTooLong')));
    else {
      setDisplayValue(stringToPhone(clearValue));
      onSubmit ? onSubmit() : spreadsheetContext.onSubmit?.();
    }
  }

  function handleOnChange(newValue: string) {
    const clearValue = getClearPhone(newValue);

    setDisplayValue(newValue);
    onChange ? onChange(clearValue) : spreadsheetContext.onChange?.(clearValue);
  }

  function handleOnKeyDown(e: KeyboardEvent<HTMLInputElement>) {
    if (e.key.length !== 1 || e.ctrlKey || e.metaKey) return;

    const isPlusAllowed =
      !displayValue.length ||
      (!e.currentTarget.selectionStart && displayValue[e.currentTarget.selectionEnd ?? 0] !== '+');

    if ((e.key === '+' && !isPlusAllowed) || !e.key.match(/[\d()-. ]/)) e.preventDefault();
  }

  return { handleOnSubmit, handleOnChange, handleOnKeyDown, displayValue };
}
